import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEO from '../components/SEO';

// Icons
const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ZapIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const PaintBrushIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9.06 11.9 8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08" />
    <path d="m7.07 14.94-1.13 1.13a3.5 3.5 0 0 1-4.95 0 3.5 3.5 0 0 1 0-4.94l1.45-1.45" />
    <path d="m2 21 6-6" />
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PainterQuotingSoftware = () => {
  useEffect(() => {
    analytics.track('SEO Page View', { page: 'Painter Quoting Software' });
  }, []);

  return (
    <>
      <SEO
        title="Painting Quote Software | Professional Quotes for Painters | QuoteTier"
        description="Create professional painting quotes with Good/Better/Best pricing tiers. Let customers choose paint quality, coats, and warranty options. Start free."
        canonical="/painting-quote-software"
        noindex={true}
      />
      <div className="min-h-screen bg-white">
        <header className="border-b border-gray-200">
          <div className="container max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ZapIcon className="h-6 w-6 text-primary" />
              <span className="font-instrument text-2xl">QuoteTier</span>
            </Link>
            <Link to="/signup">
              <Button className="bg-black text-white hover:bg-gray-800 font-instrument">
                Start Free
              </Button>
            </Link>
          </div>
        </header>

        <main>
          <section className="py-24 md:py-32">
            <div className="container max-w-7xl mx-auto px-4 text-center">
              <h1 className="font-instrument text-5xl md:text-6xl lg:text-7xl tracking-tight mb-6">
                <PaintBrushIcon className="inline-block h-12 w-12 md:h-16 md:w-16 mr-4" />
                Painting Quote Software
              </h1>
              <p className="font-jetbrains text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-10">
                Create professional painting quotes that let customers choose their paint quality, number of coats, and warranty options. Watch your average job size increase.
              </p>
              <Link to="/signup">
                <Button className="px-8 py-4 text-xl font-instrument bg-black text-white hover:bg-gray-800">
                  Start Free - 3 Quotes/Month
                </Button>
              </Link>
            </div>
          </section>

          <section className="py-16 bg-gray-50">
            <div className="container max-w-5xl mx-auto px-4">
              <h2 className="font-instrument text-3xl md:text-4xl text-center mb-12">
                Why Painters Choose QuoteTier
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="font-instrument">Good/Better/Best Pricing</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="font-jetbrains">
                      Present paint quality tiers clearly. Customers see exactly what they get with each option and often choose the premium tier.
                    </CardDescription>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="font-instrument">E-Signatures Built-In</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="font-jetbrains">
                      Get legally binding signatures on the spot. No more chasing down paperwork or waiting for customers to return signed quotes.
                    </CardDescription>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="font-instrument">Mobile-Optimized</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="font-jetbrains">
                      Your quotes look great on any device. Customers can review and sign from their phone right after you leave the job site.
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          <section className="py-16">
            <div className="container max-w-4xl mx-auto px-4 text-center">
              <h2 className="font-instrument text-3xl md:text-4xl mb-6">
                Ready to grow your painting business?
              </h2>
              <p className="font-jetbrains text-xl text-gray-600 mb-8">
                Join painters who have increased their average job size with interactive quotes.
              </p>
              <Link to="/signup">
                <Button className="px-10 py-4 text-xl font-instrument bg-black text-white hover:bg-gray-800">
                  Get Started Free
                </Button>
              </Link>
            </div>
          </section>
        </main>
      </div>
    </>
  );
};

export default PainterQuotingSoftware;
